// packages/core/src/learning/school-modules/crypto-web3.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'crypto-web3' school. LearningModule, surface 'school'.
//   module id   = 'school-crypto-web3'  (matches exhibit nativeModuleId)
//   homeSchool  = 'crypto-web3'
// PROMOTED: five authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_CRYPTO_WEB3_MODULE: LearningModule = {
	id: 'school-crypto-web3',
	title: 'Crypto & Web3',
	description:
		'The lens that questions who controls the rails — custody as responsibility, separating the technology thesis from the token, hidden incentives, and where belief turns into fragile speculation.',
	emoji: '⛓️',
	accentColor: '#d65a3a',
	tier: 'growth',
	pillar: 'long-term-investing',
	order: 0,
	surface: 'school',
	homeSchool: 'crypto-web3',
	lessons: [
		{
			id: 'school-crypto-web3-01',
			title: 'Crypto questions who controls the rails',
			description:
				'This lesson teaches how crypto and web3 sees money as a system of trust, access, custody, and control.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['crypto-web3'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['crypto-web3', 'control-of-rails', 'open-networks'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['control of rails', 'trust design', 'open networks'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Most people use money systems they did not design. Banks, card networks, brokerages, apps, and platforms decide the rules in the background.',
				},
				{
					type: 'paragraph',
					text: 'Crypto and web3 is paying attention to the rails underneath money and ownership. This school asks what happens when some trust moves away from institutions and toward public networks, code, cryptography, and user custody.',
				},
				{
					type: 'bullet_list',
					items: [
						'Who controls access?',
						'Who writes the rules?',
						'Who can freeze or change the system?',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking crypto is only about prices going up or down.',
				},
				{
					type: 'example',
					text: 'A normal app can close an account, reverse access, or change terms. A crypto network may let users hold assets directly, but that also means the user may carry more responsibility if something goes wrong.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is serious. Removing a gatekeeper can also remove customer support, refunds, reversals, and familiar protection.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens starts with a systems question, not a buying question. You are studying what this worldview notices before you judge whether the risk belongs in your life.',
				},
				{
					type: 'key_takeaway',
					text: 'Crypto and web3 starts with control of the rails, not token excitement.',
				},
			],
			audioScript:
				'Crypto and web3 makes more sense when you stop treating it as only a price chart. The deeper worldview is about control. Who controls the money rails? Who can give access? Who can remove access? Who writes the rules? Who gets trusted by default?\n\nMost people do not think about this because the normal financial system feels invisible when it works. You swipe a card. You receive a paycheck. You log into an app. You keep money at a bank. You send a payment. A lot of institutions, rules, databases, processors, and legal systems are underneath that simple experience. When everything works, it feels normal. When it fails, gets blocked, gets delayed, or excludes someone, the rails become visible.\n\nCrypto and web3 is paying attention to that hidden layer. It asks whether some systems can be built where the rules are more public, where access is not controlled by one company, where ownership can sit closer to the user, and where value can move across networks without asking the same old gatekeepers for permission.\n\nThat does not automatically make it safe. This is where the lens has to stay honest. Moving control away from institutions can also move responsibility onto the user. If you forget a bank password, there may be a reset process. If you lose a private key, there may be no one to call. If a bank detects fraud, there may be protections. If you sign a bad transaction, the loss may be permanent. If a platform fails, the legal system may give you some path. If a decentralized system fails, that path may be unclear or nonexistent.\n\nSo the tradeoff is not old system bad, new system good. That is too childish. The better framing is this: centralized systems offer convenience, support, and guardrails, but they concentrate power. Decentralized systems may offer more direct ownership and open access, but they can remove protections people are used to.\n\nThat is why this school is useful even for people who never touch a token. It teaches you to see money as infrastructure. It makes you ask who controls the account, who can change the rules, what protections exist, and what responsibility shifts to the user.\n\nBut the danger is that this systems question gets buried under speculation. People hear “new rails” and immediately jump to “number goes up.” That is where the worldview gets sloppy. A network idea can be interesting while a token is still risky. A technology can matter while many projects fail. A belief in decentralization does not erase volatility, scams, hacks, or bad incentives.\n\nInside Nisk, treat this as a lens. You are not being told what to buy, sell, hold, borrow, or invest in. You are learning what crypto and web3 notices. It notices control, access, custody, and trust design. It also accepts risk that normal users can underestimate.\n\nThe takeaway is simple. Crypto and web3 is not only about assets. It is about who controls the rails, and what you give up when control moves closer to you.',
			flashcards: [
				{
					id: 'school-crypto-web3-01-fc-1',
					role: 'main_idea',
					front: 'What does crypto notice first?',
					back: 'It notices who controls money, access, ownership, and the rules.',
				},
				{
					id: 'school-crypto-web3-01-fc-2',
					role: 'watch_out',
					front: 'What is the control tradeoff?',
					back: 'More control can also mean fewer protections and more personal responsibility.',
				},
				{
					id: 'school-crypto-web3-01-fc-3',
					role: 'use_it',
					front: 'How do you study this lens?',
					back: 'Ask who controls access, who writes rules, and who carries the risk.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Map the rails',
				prompt: 'Write who controls access in one money app or platform you already use.',
			},
			revision: 1,
		},
		{
			id: 'school-crypto-web3-02',
			title: 'Ownership can become responsibility',
			description:
				'This lesson teaches how crypto and web3 treats custody as power, with real user risk attached.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['crypto-web3'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['crypto-web3', 'custody-responsibility', 'private-key-risk'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'custody responsibility',
				'private key risk',
				'user control',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Owning something directly sounds powerful until something breaks. Crypto and web3 forces that tension into the open.',
				},
				{
					type: 'paragraph',
					text: 'This school believes custody matters. If you control the key, account, wallet, or asset directly, you may depend less on a company. But direct control can also mean direct consequences.',
				},
				{
					type: 'bullet_list',
					items: [
						'Lost keys can mean lost access',
						'Bad signatures can move funds',
						'No support desk may exist',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating self custody like a normal app login.',
				},
				{
					type: 'example',
					text: 'If someone loses access to a normal account, there may be password recovery. If someone loses access to a self custody wallet, the asset may be gone for practical purposes.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is underestimating how much ordinary users rely on friction, fraud checks, and human support.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to runway and risk capacity. Before any high responsibility system makes sense, the user should understand how much mistake risk their real life can absorb.',
				},
				{
					type: 'key_takeaway',
					text: 'In crypto and web3, ownership can give control, but control can remove rescue.',
				},
			],
			audioScript:
				'Crypto and web3 talks a lot about ownership. Own your assets. Own your identity. Own your data. Own your money. That sounds clean and powerful. But the real lesson is more complicated: ownership can become responsibility very fast.\n\nIn the normal online world, many things are controlled by companies. That can be frustrating. A company can close an account, change terms, limit access, or make decisions you disagree with. But those same companies also provide support. They reset passwords. They reverse some mistakes. They monitor suspicious activity. They create friction before money moves. They give users familiar guardrails.\n\nCrypto and web3 often asks what happens when the user has more direct control. Instead of relying fully on a platform account, the user may control a wallet. Instead of a company changing an internal database, a transaction may settle on a public network. Instead of asking permission from a central operator, the user may interact directly with a protocol.\n\nThat can be empowering. It can also be unforgiving.\n\nThe custody issue is one of the clearest examples. If you control the keys, you may have more direct ownership. But if those keys are lost, stolen, phished, or mismanaged, there may be no simple recovery process. A bank password and a private key are not the same experience. A normal app login usually assumes humans forget things. Self custody often assumes the user is responsible for protecting access.\n\nThat changes the meaning of risk. Risk is not only price volatility. It is also operational risk. Did you store the key safely? Did you sign the right transaction? Did you interact with the real site or a fake one? Did you understand what permissions you gave? Did you protect yourself from scams, malware, and social pressure?\n\nThis is where the worldview has a blind spot. People who are deep in crypto can forget how much normal users rely on support, error messages, chargebacks, fraud teams, and account recovery. They may say, “Be your own bank,” without respecting how hard banking operations actually are. A person may want more control, but that does not mean they are ready for every responsibility that comes with it.\n\nInside Nisk, this is why crypto should be taught with caution. The goal is not to scare the user or hype the user. The goal is to make the tradeoff visible. If a system gives you more control, ask what protection disappeared. If a system gives you direct ownership, ask what mistake could become permanent.\n\nThis connects to real life planning too. If your runway is thin, your stress is high, or one mistake could damage your stability, high responsibility systems deserve extra caution. The issue is not whether the technology is interesting. The issue is whether the responsibility fits your current life.\n\nThe takeaway is simple. In crypto and web3, custody is not a feature you can ignore. Ownership can give control, but control can remove rescue.',
			flashcards: [
				{
					id: 'school-crypto-web3-02-fc-1',
					role: 'main_idea',
					front: 'Why does custody matter?',
					back: 'It decides who controls access and who carries responsibility.',
				},
				{
					id: 'school-crypto-web3-02-fc-2',
					role: 'watch_out',
					front: 'What is the custody trap?',
					back: 'Treating a self custody wallet like a normal recoverable login.',
				},
				{
					id: 'school-crypto-web3-02-fc-3',
					role: 'use_it',
					front: 'What should you ask?',
					back: 'Ask what happens if access is lost, stolen, or signed away.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
			revision: 1,
		},
		{
			id: 'school-crypto-web3-03',
			title: 'The technology thesis is not the token',
			description:
				'This lesson teaches why crypto and web3 separates belief in new rails from speculation on token prices.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['crypto-web3'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['crypto-web3', 'technology-token-split', 'speculation-risk'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'technology token split',
				'speculation risk',
				'thesis separation',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'A new technology can be real while many tokens connected to it still disappoint. Crypto and web3 gets dangerous when people blur that line.',
				},
				{
					type: 'paragraph',
					text: 'This school believes open networks may change how money, ownership, communities, and applications work online. But a strong technology thesis does not automatically make any token a strong outcome.',
				},
				{
					type: 'bullet_list',
					items: [
						'Networks can matter',
						'Tokens can fall',
						'Projects can fail',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is believing a token must win because the idea sounds important.',
				},
				{
					type: 'example',
					text: 'The internet changed the world, but many internet companies still failed. A similar lesson applies here. A real technology wave does not make every project durable or every price sensible.',
				},
				{
					type: 'paragraph',
					text: 'This is speculation. Losses are possible, outcomes are not reliable, and token prices can move far faster than the underlying usefulness of the network.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to separating thesis from exposure. Write what you believe about the technology, then write what could still go wrong with the asset.',
				},
				{
					type: 'key_takeaway',
					text: 'You can respect the technology thesis without pretending token outcomes are reliable.',
				},
			],
			audioScript:
				'One of the most important lessons in crypto and web3 is that the technology thesis and the token outcome are not the same thing.\n\nA person can believe that open networks matter. They can believe digital ownership matters. They can believe decentralized finance, programmable agreements, or new coordination systems are worth studying. That does not automatically mean any specific token, project, or price will work out.\n\nThis is where many people get pulled into bad thinking. They hear a big idea and treat it like an investment conclusion. They say the world is becoming more digital, so this token must rise. They say ownership matters, so this project must win. They say the old system is flawed, so the new system must be valuable. That is not analysis. That is a story jumping over the hard part.\n\nHistory gives a useful pattern. The internet was real. It changed business, media, communication, and commerce. But many internet companies failed. Some were bad ideas. Some were too early. Some were real businesses bought at absurd prices. Some had users but no durable economics. Being right about the broad technology did not mean every investment worked.\n\nCrypto and web3 needs the same humility. A network may be useful while a token still performs poorly. A project may attract attention but fail to create lasting value. A token may rise because of hype, liquidity, or speculation, then fall when attention moves elsewhere. A community may sound strong while incentives are actually fragile. A product may be technically clever but still not solve a painful enough problem for enough people.\n\nThe risk is not theoretical. Losses are possible. Outcomes are not reliable. Speculation can move violently. Token prices can change faster than a normal person can emotionally process. Scams, hacks, regulation changes, bad incentives, weak governance, and custody mistakes can all damage the result.\n\nThat is why this lesson is central to the crypto lens. The mature version separates three things. First, what do you believe about the technology? Second, what do you believe about a specific project or network? Third, what does the current price already assume? Those are different questions.\n\nInside Nisk, this is not about telling you what to buy, sell, hold, borrow, or invest in. It is about building judgment. A person can study crypto without being recruited into token speculation. A person can believe the old financial system has flaws without pretending every alternative is safe. A person can be curious without being careless.\n\nThe clean habit is to write the thesis before the excitement takes over. “Here is what I think this technology changes.” Then write the separate risk case. “Here is why the asset could still fail.” That second sentence is where better judgment starts.\n\nThe takeaway is direct. The technology can matter while the token still disappoints. Crypto and web3 is most useful when it keeps that separation clear.',
			flashcards: [
				{
					id: 'school-crypto-web3-03-fc-1',
					role: 'main_idea',
					front: 'What must crypto separate?',
					back: 'The technology thesis and the token outcome.',
				},
				{
					id: 'school-crypto-web3-03-fc-2',
					role: 'watch_out',
					front: 'What is the token trap?',
					back: 'Believing a token must rise because the technology sounds important.',
				},
				{
					id: 'school-crypto-web3-03-fc-3',
					role: 'use_it',
					front: 'How do you stay clear?',
					back: 'Write the technology case and the asset risk as separate thoughts.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Split thesis and token',
				prompt: 'Write one technology belief, then write three reasons a related token could still fail.',
			},
			revision: 1,
		},
		{
			id: 'school-crypto-web3-04',
			title: 'Open networks can hide bad incentives',
			description:
				'This lesson teaches how crypto and web3 notices coordination, but can underestimate scams, incentive games, and weak governance.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['crypto-web3'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['crypto-web3', 'incentive-design', 'governance-risk'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['incentive design', 'governance risk', 'scam pressure'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Crypto and web3 often talks about communities, protocols, and open participation. That can sound more democratic than companies, but incentives still matter.',
				},
				{
					type: 'paragraph',
					text: 'This school notices that tokens can coordinate people around a shared network. The same design can also attract speculators, insiders, scammers, and short term behavior.',
				},
				{
					type: 'bullet_list',
					items: [
						'Incentives shape behavior',
						'Communities can be fragile',
						'Scams use urgency',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is assuming open means honest.',
				},
				{
					type: 'example',
					text: 'A project may claim to be community owned, but a small group may hold most of the tokens, control decisions, or benefit most from hype. The language sounds open while the incentives are concentrated.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is trust theater. A system can use decentralization language while still depending on insiders, weak governance, or users who do not understand the risk.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this connects to slowing down when pressure appears. If a decision requires speed, secrecy, or blind belief, the risk deserves attention.',
				},
				{
					type: 'key_takeaway',
					text: 'Open networks still need hard questions about incentives, control, and scams.',
				},
			],
			audioScript:
				'Crypto and web3 often uses words that sound empowering: community, ownership, decentralization, open access, governance, protocol. Those words can point to real ideas. They can also hide bad incentives.\n\nThis is where the lens needs maturity. Open does not automatically mean honest. Community does not automatically mean fair. Decentralized does not automatically mean safe. A project can use the language of user ownership while still concentrating power, benefits, and information in the hands of a small group.\n\nCrypto and web3 is interesting partly because tokens can coordinate behavior. A token can give people a reason to participate in a network, support a project, vote on decisions, provide liquidity, or build around a protocol. In theory, this can create a new kind of online organization. Users are not just customers. They may become participants.\n\nBut incentives can cut both ways. A token can encourage real contribution, or it can encourage hype. A community can become a place to build, or a place where people pressure each other to keep believing. Governance can give users a voice, or it can become controlled by a few large holders. Transparency can reveal useful information, or it can overwhelm normal users who do not know what they are looking at.\n\nScams also understand this language. They use urgency. They use insider access. They use fear of missing out. They use technical words to make people feel behind. They use screenshots, fake communities, fake endorsements, and complicated structures to create pressure. The promise is often some version of, “Get in before everyone else understands.” That is dangerous.\n\nA realistic example: a project says it is community owned. The website looks polished. The chat is active. People talk about changing finance forever. But a small group may control a large share of tokens. The code may be untested. The governance may be performative. The business model may depend on new buyers entering. The community may punish honest questions because doubt threatens the price.\n\nThat does not mean every crypto community is fake. It means the same human behavior exists in new systems. Greed, fear, status, manipulation, and group pressure do not disappear because the technology is different.\n\nInside Nisk, this lesson connects to decision pressure. Whenever a money idea demands speed, secrecy, or blind loyalty, slow down. If questions are treated like betrayal, that is information. If risk is brushed aside because the community feels strong, that is information. If the only answer to a concern is “you just do not understand,” that is information too.\n\nThe mature crypto and web3 lens asks hard questions. Who benefits? Who controls the supply? Who can change the rules? Who has better information than ordinary users? What happens if the hype leaves? What protections exist if the project fails?\n\nThe takeaway is simple. Open networks can be powerful, but incentives still run the room. Study the incentives before you trust the story.',
			flashcards: [
				{
					id: 'school-crypto-web3-04-fc-1',
					role: 'main_idea',
					front: 'What can tokens coordinate?',
					back: 'Participation, governance, liquidity, building, and community behavior.',
				},
				{
					id: 'school-crypto-web3-04-fc-2',
					role: 'watch_out',
					front: 'What is trust theater?',
					back: 'Decentralization language that hides concentrated control or weak incentives.',
				},
				{
					id: 'school-crypto-web3-04-fc-3',
					role: 'use_it',
					front: 'What pressure signal matters?',
					back: 'Speed, secrecy, blind loyalty, and punishment of questions deserve caution.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Check the incentives',
				prompt: 'Write who benefits if a crypto project gains attention, then write who carries the downside if it fails.',
			},
			revision: 1,
		},
		{
			id: 'school-crypto-web3-05',
			title: 'Where crypto and web3 breaks',
			description:
				'This lesson teaches how the crypto and web3 lens can turn into hype, overconfidence, and fragile speculation.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['crypto-web3'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['crypto-web3', 'hype-cycle', 'permanent-loss'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['hype cycle', 'permanent loss', 'worldview humility'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Crypto and web3 breaks when belief becomes identity. Once that happens, every risk sounds like an attack.',
				},
				{
					type: 'paragraph',
					text: 'This school is useful because it notices real questions about trust, ownership, access, and centralized power. But it can go wrong when those questions become a reason to ignore volatility, scams, hacks, custody mistakes, regulation risk, and possible losses.',
				},
				{
					type: 'bullet_list',
					items: [
						'Curiosity is not conviction',
						'Volatility can be brutal',
						'Total loss is possible',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating skepticism as ignorance instead of risk awareness.',
				},
				{
					type: 'example',
					text: 'Someone may believe open networks matter, then keep defending a failing project because admitting risk feels like betraying the movement. The worldview becomes a shield against evidence.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is turning a useful critique of the old system into blind faith in the new one.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should end with humility. Study the worldview, name the risks, and separate curiosity from exposure.',
				},
				{
					type: 'key_takeaway',
					text: 'Crypto and web3 is strongest when curiosity stays separate from blind belief.',
				},
			],
			audioScript:
				'Crypto and web3 breaks when belief becomes identity. That is the point where a worldview stops helping you think and starts protecting your ego.\n\nThe useful part of the lens is real. It asks hard questions about centralized power. It asks who controls the rails. It asks what ownership means online. It asks whether value can move through open networks instead of only through banks, platforms, brokers, and old institutions. Those are serious questions. They are worth understanding.\n\nBut serious questions do not automatically create safe outcomes. Crypto and web3 can become dangerous when people use the flaws of the old system to excuse every weakness in the new one. A bank can be flawed, and a crypto project can still be reckless. A platform can abuse power, and a token can still collapse. A government currency can lose purchasing power, and a digital asset can still lose most of its value. Both things can be true.\n\nThis is where risk framing has to be direct. Losses are possible. Outcomes are not reliable. Speculation is not a dependable path to wealth. A project can fail. A wallet can be compromised. A smart contract can break. A founder can lie. A community can become a pressure chamber. A token can fall hard and never recover. Regulation can change the rules. Liquidity can disappear when users need it most.\n\nThe emotional danger is that crypto often combines money, identity, technology, and ideology. That mix is powerful. People may not just think they own an asset. They may feel like they are part of a movement. Then criticism feels personal. Skepticism gets dismissed as ignorance. Risk warnings get treated like fear. The person stops asking, “Is this true?” and starts asking, “Are you on our side?”\n\nThat is not good judgment. That is tribal thinking.\n\nA realistic example is someone who believes open networks matter, then attaches that belief to one project. At first, they are curious. Then the price rises, the community gets louder, and the story becomes part of their identity. When bad news appears, they explain it away. When the project misses goals, they blame outsiders. When losses appear, they say critics do not understand. The worldview becomes a shield against evidence.\n\nInside Nisk, the goal is to keep the lens clean. You can study crypto without becoming loyal to a token. You can respect decentralization without ignoring scams. You can be curious about new rails without pretending the future is guaranteed. You can think the old system has problems without assuming every alternative is better.\n\nThe best version of this worldview sounds sober: “Here is what crypto notices. Here is what the old system misses. Here is what the new system risks. Here is what could fail. Here is what I do not know.”\n\nThat is the posture that protects judgment. Not hype. Not hatred. Not blind belief. Clear curiosity with risk fully named.\n\nThe takeaway is simple. Crypto and web3 is strongest when curiosity stays separate from identity, speculation, and denial.',
			flashcards: [
				{
					id: 'school-crypto-web3-05-fc-1',
					role: 'main_idea',
					front: 'Where does crypto break?',
					back: 'When belief becomes identity and risk warnings get ignored.',
				},
				{
					id: 'school-crypto-web3-05-fc-2',
					role: 'watch_out',
					front: 'What risk must be named?',
					back: 'Losses, scams, hacks, custody mistakes, regulation risk, and project failure.',
				},
				{
					id: 'school-crypto-web3-05-fc-3',
					role: 'use_it',
					front: 'How do you stay sober?',
					back: 'Separate curiosity about the worldview from exposure to speculation.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Separate curiosity from risk',
				prompt: 'Write what interests you about crypto, then write what risk would make it unsuitable for your current life.',
			},
			revision: 1,
		},
	],
};
